import { useEffect, useState } from 'react';
import Modal from './ui/Modal.jsx';
import { pb } from '../lib/pocketbase.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PARTNER_TYP_OPTIONS } from '../lib/partner.js';

const EMPTY = {
  name: '',
  typ: 'externer_vertriebspartner',
  kontaktperson: '',
  telefon: '',
  email: '',
  website: '',
  notizen: '',
  partner_strasse: '',
  partner_plz: '',
  partner_ort: '',
  partner_bundesland: '',
  partner_land: '',
};

export default function PartnerFormModal({ open, onClose, onSaved, partner }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Beim Neuanlegen "Deutschland" als Land vorbelegen (Bestandsdaten
    // bleiben unverändert — Migration hat bewusst nichts geschrieben).
    setForm(partner ? { ...EMPTY, ...partner } : { ...EMPTY, partner_land: 'Deutschland' });
    setError(null);
  }, [open, partner]);

  function handleChange(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (form.website && form.website.trim() && !form.website.includes('.')) {
      setError('Die Website sieht nicht wie eine URL aus (kein Punkt enthalten).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name,
        typ: form.typ,
        kontaktperson: form.kontaktperson,
        telefon: form.telefon,
        email: form.email,
        website: form.website,
        notizen: form.notizen,
        partner_strasse: form.partner_strasse,
        partner_plz: form.partner_plz,
        partner_ort: form.partner_ort,
        partner_bundesland: form.partner_bundesland,
        partner_land: form.partner_land,
      };
      let saved;
      if (partner?.id) {
        saved = await pb.collection('partner').update(partner.id, payload);
      } else {
        payload.angelegt_von = user?.id ?? null;
        saved = await pb.collection('partner').create(payload);
      }
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={partner?.id ? 'Partner bearbeiten' : 'Neuen Partner anlegen'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label">Name *</label>
            <input
              type="text"
              required
              className="input mt-1"
              value={form.name}
              onChange={handleChange('name')}
            />
          </div>

          <div>
            <label className="label">Typ *</label>
            <select required className="input mt-1" value={form.typ} onChange={handleChange('typ')}>
              {PARTNER_TYP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Kontaktperson</label>
            <input
              type="text"
              className="input mt-1"
              value={form.kontaktperson}
              onChange={handleChange('kontaktperson')}
            />
          </div>

          <div>
            <label className="label">Telefon</label>
            <input
              type="tel"
              className="input mt-1"
              value={form.telefon}
              onChange={handleChange('telefon')}
            />
          </div>

          <div>
            <label className="label">E-Mail</label>
            <input
              type="email"
              className="input mt-1"
              value={form.email}
              onChange={handleChange('email')}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="label">Website</label>
            <input
              type="text"
              className="input mt-1"
              placeholder="z. B. www.steuerkanzlei-musterstadt.de"
              value={form.website}
              onChange={handleChange('website')}
            />
          </div>

          <div className="sm:col-span-2 mt-2 border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold text-slate-700">Adresse</h3>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Straße</label>
            <input
              type="text"
              className="input mt-1"
              value={form.partner_strasse}
              onChange={handleChange('partner_strasse')}
            />
          </div>

          <div>
            <label className="label">PLZ</label>
            <input
              type="text"
              className="input mt-1"
              value={form.partner_plz}
              onChange={handleChange('partner_plz')}
            />
          </div>

          <div>
            <label className="label">Ort</label>
            <input
              type="text"
              className="input mt-1"
              value={form.partner_ort}
              onChange={handleChange('partner_ort')}
            />
          </div>

          <div>
            <label className="label">Bundesland</label>
            <input
              type="text"
              className="input mt-1"
              value={form.partner_bundesland}
              onChange={handleChange('partner_bundesland')}
            />
          </div>

          <div>
            <label className="label">Land</label>
            <input
              type="text"
              className="input mt-1"
              value={form.partner_land}
              onChange={handleChange('partner_land')}
            />
          </div>

          <div className="sm:col-span-2 mt-2 border-t border-slate-200 pt-4">
            <label className="label">Notizen</label>
            <textarea
              rows={4}
              className="input mt-1"
              value={form.notizen}
              onChange={handleChange('notizen')}
            />
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
